import React, { useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Printer,
  CreditCard,
  MapPin,
  Phone,
  Mail,
  Scan,
} from "lucide-react";
import { infoToast } from "../../../../services/toast.service";
import { dummyProducts } from "../../../../dummyData";

const InvoiceView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  // Updated invoice data for a salwar suit wholesaler
  const invoiceData = {
    gstin: "23AATFM4928R1ZB",
    firmName: "MAHAKALI ETHNIC COLLECTION",
    address: "Shop No. 25, Textile Market, M.G. Road, Indore, MP - 452001",
    email: "mahakali.ethnic@gmail.com",
    phone: "9876543210",
    invoiceNumber: invoiceId || "ME/2023-24/256",
    date: "15-09-2023",
    placeOfSupply: "Madhya Pradesh (23)",
    reverseCharge: "N",
    billedTo: {
      name: "Fashion Paradise",
      address: "32, New Market, Bhopal, MP - 462001",
      gstin: "23AABCF7621R1Z7",
    },
    shippedTo: {
      name: "Fashion Paradise",
      address: "32, New Market, Bhopal, MP - 462001",
      gstin: "23AABCF7621R1Z7",
    },
    bankDetails: {
      accountName: "MAHAKALI ETHNIC COLLECTION",
      accountNumber: "1234567890123456",
      ifscCode: "SBIN0012345",
      bankName: "State Bank of India",
      branch: "M.G. Road, Indore",
    },
    upiId: "mahakali.ethnic@upi",
    items: [
      {
        sn: 1,
        description: "Chikankari Embroidered Salwar Suit (Pink)",
        size: "S, M, L, XL",
        hsnCode: "6204",
        qty: 24,
        unit: "PCS",
        price: 1899.0,
        amount: 45576.0,
      },
      {
        sn: 2,
        description: "Anarkali Style Cotton Suit (Blue)",
        size: "M, L, XL",
        hsnCode: "6204",
        qty: 12,
        unit: "PCS",
        price: 1499.0,
        amount: 17988.0,
      },
      {
        sn: 3,
        description: "Designer Palazzo Suit (Mint Green)",
        size: "S, M, L",
        hsnCode: "6204",
        qty: 18,
        unit: "PCS",
        price: 2199.0,
        amount: 39582.0,
      },
      {
        sn: 4,
        description: "Heavy Work Patiala Suit (Maroon)",
        size: "M, L, XL",
        hsnCode: "6204",
        qty: 6,
        unit: "PCS",
        price: 2399.0,
        amount: 14394.0,
      },
      {
        sn: 5,
        description: "Printed Cotton Daily Wear Suit (Yellow)",
        size: "XS, S, M, L, XL",
        hsnCode: "6204",
        qty: 30,
        unit: "PCS",
        price: 999.0,
        amount: 29970.0,
      },
    ],
    totalQty: 90,
    totalAmount: 147510.0,
    discount: {
      percentage: 10,
      amount: 14751.0,
    },
    sgst: {
      percentage: 6,
      amount: 7965.54,
    },
    cgst: {
      percentage: 6,
      amount: 7965.54,
    },
    roundedOff: -0.08,
    grandTotal: 148690.0,
    amountInWords:
      "Rupees One Lakh Forty Eight Thousand Six Hundred Ninety Only",
    taxRate: "12%",
    taxableAmount: 132759.0,
    sgstAmt: 7965.54,
    cgstAmt: 7965.54,
    totalTax: 15931.08,
    terms: [
      "E & O.E.",
      "Goods once sold will not be taken back.",
      "Interest @ 18% p.a. will be charged if the payment is not made within the stipulated time.",
      "All disputes are subject to 'Indore, Madhya Pradesh' Jurisdiction only.",
      "Returns accepted only within 7 days of delivery with original packaging.",
    ],
  };

  // Modified handlers to display toast messages
  const handlePrint = () => {
    infoToast("Print functionality disabled in demo version");
  };

  const handleDownload = () => {
    infoToast("Download functionality disabled in demo version");
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Function to generate a placeholder QR code (for demo purposes)
  const generateQrCodeUrl = () => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=upi://pay?pa=${
      invoiceData.upiId
    }%26pn=${encodeURIComponent(invoiceData.firmName)}%26am=${
      invoiceData.grandTotal
    }%26cu=INR`;
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-6">
      {/* Non-printable controls */}
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>
        <div className="flex gap-4">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-md border border-gray-300 cursor-not-allowed opacity-70">
            <Printer size={16} />
            <span>Print (Disabled)</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed opacity-70">
            <Download size={16} />
            <span>Download (Disabled)</span>
          </button>
        </div>
      </div>

      {/* Invoice Card - A4 size optimized with compact layout */}
      <div
        ref={invoiceRef}
        className="bg-white shadow-lg mx-auto border border-gray-200 w-full max-w-[210mm]">
        {/* Invoice Header - More compact */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 py-4 px-5 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <div className="mb-4 md:mb-0">
              <h1 className="text-xl font-bold text-[#2A5783] uppercase">
                {invoiceData.firmName}
              </h1>
              <div className="flex flex-wrap text-xs text-gray-600 mt-1">
                <p className="mr-4 flex items-center">
                  <MapPin size={12} className="mr-1 flex-shrink-0" />
                  {invoiceData.address}
                </p>
                <p className="mr-4 flex items-center">
                  <Phone size={12} className="mr-1 flex-shrink-0" />
                  {invoiceData.phone}
                </p>
                <p className="flex items-center">
                  <Mail size={12} className="mr-1 flex-shrink-0" />
                  {invoiceData.email}
                </p>
              </div>
              <p className="text-xs font-medium mt-1 text-gray-700">
                GSTIN: {invoiceData.gstin}
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-[#2A5783] rounded-lg flex items-center justify-center text-white text-3xl font-bold">
                M<span className="text-xs mt-2">EC</span>
              </div>
              <div className="mt-1 text-center">
                <h2 className="text-lg font-bold uppercase bg-[#2A5783] text-white px-3 py-0.5 rounded">
                  Tax Invoice
                </h2>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-3 text-xs">
            <div className="space-y-1">
              <div className="flex">
                <span className="font-semibold w-24 text-[#2A5783]">
                  Invoice No.
                </span>
                <span className="font-medium">
                  : {invoiceData.invoiceNumber}
                </span>
              </div>
              <div className="flex">
                <span className="font-semibold w-24 text-[#2A5783]">Dated</span>
                <span className="font-medium">: {invoiceData.date}</span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex">
                <span className="font-semibold w-32 text-[#2A5783]">
                  Place of Supply
                </span>
                <span className="font-medium">
                  : {invoiceData.placeOfSupply}
                </span>
              </div>
              <div className="flex">
                <span className="font-semibold w-32 text-[#2A5783]">
                  Reverse Charge
                </span>
                <span className="font-medium">
                  : {invoiceData.reverseCharge}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details - Compact */}
        <div className="py-3 px-5 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
          <div className="border-l-4 border-[#2A5783] pl-3">
            <h3 className="font-bold mb-1 text-[#2A5783] text-xs">
              Billed to:
            </h3>
            <p className="font-medium text-xs">{invoiceData.billedTo.name}</p>
            <p className="text-gray-600 text-xs">
              {invoiceData.billedTo.address}
            </p>
            <div className="mt-1">
              <span className="font-semibold text-[#2A5783] text-xs">
                GSTIN / UIN
              </span>
              <span className="ml-1 text-xs">
                : {invoiceData.billedTo.gstin}
              </span>
            </div>
          </div>
          <div className="border-l-4 border-[#2A5783] pl-3">
            <h3 className="font-bold mb-1 text-[#2A5783] text-xs">
              Shipped to:
            </h3>
            <p className="font-medium text-xs">{invoiceData.shippedTo.name}</p>
            <p className="text-gray-600 text-xs">
              {invoiceData.shippedTo.address}
            </p>
            <div className="mt-1">
              <span className="font-semibold text-[#2A5783] text-xs">
                GSTIN / UIN
              </span>
              <span className="ml-1 text-xs">
                : {invoiceData.shippedTo.gstin}
              </span>
            </div>
          </div>
        </div>

        {/* Invoice Items - Compact row heights */}
        <div className="py-3 px-5 border-b border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 text-left text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  S.N.
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Description of Goods
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Size
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  HSN/SAC
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Qty.
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Price
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-[#2A5783] uppercase tracking-wider">
                  Amount(₹)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoiceData.items.map((item, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-700">
                    {item.sn}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-700 text-center">
                    {item.description}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-700 text-center">
                    {item.size}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs text-gray-700">
                    {item.hsnCode}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs text-gray-700">
                    {item.qty.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs text-gray-700">
                    {item.unit}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs text-gray-700">
                    {item.price.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right text-xs text-gray-700">
                    {item.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50">
                <td
                  colSpan={4}
                  className="px-2 py-2 whitespace-nowrap text-right text-xs font-bold text-[#2A5783]">
                  Total
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-center text-xs font-bold text-[#2A5783]">
                  {invoiceData.totalQty}.00
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-center text-xs font-bold text-[#2A5783]">
                  PCS
                </td>
                <td colSpan={1}></td>
                <td className="px-2 py-2 whitespace-nowrap text-right text-xs font-bold text-[#2A5783]">
                  {invoiceData.totalAmount.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary Section with Bank Details and QR side-by-side */}
        <div className="p-3 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Side - Calculation Summary */}
          <div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#2A5783] text-white font-medium px-3 py-1 text-xs">
                Invoice Summary
              </div>

              <div className="p-3">
                <table className="min-w-full">
                  <tbody>
                    <tr>
                      <td className="py-1 w-20 text-left font-medium text-[#2A5783] text-xs">
                        Less
                      </td>
                      <td className="py-1 w-20 text-left font-medium text-xs">
                        : Discount
                      </td>
                      <td className="py-1 w-10 text-center text-xs">@</td>
                      <td className="py-1 w-16 text-right text-xs">
                        {invoiceData.discount.percentage} %
                      </td>
                      <td className="py-1 w-20 text-right font-medium text-xs">
                        {invoiceData.discount.amount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-left font-medium text-[#2A5783] text-xs">
                        Add
                      </td>
                      <td className="py-1 text-left font-medium text-xs">
                        : SGST
                      </td>
                      <td className="py-1 text-center text-xs">@</td>
                      <td className="py-1 text-right text-xs">
                        {invoiceData.sgst.percentage}.000 %
                      </td>
                      <td className="py-1 text-right font-medium text-xs">
                        {invoiceData.sgst.amount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-left font-medium text-[#2A5783] text-xs">
                        Add
                      </td>
                      <td className="py-1 text-left font-medium text-xs">
                        : CGST
                      </td>
                      <td className="py-1 text-center text-xs">@</td>
                      <td className="py-1 text-right text-xs">
                        {invoiceData.cgst.percentage}.000 %
                      </td>
                      <td className="py-1 text-right font-medium text-xs">
                        {invoiceData.cgst.amount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-left font-medium text-[#2A5783] text-xs">
                        Less
                      </td>
                      <td className="py-1 text-left font-medium text-xs">
                        : Rounded Off (-)
                      </td>
                      <td className="py-1 text-xs"></td>
                      <td className="py-1 text-xs"></td>
                      <td className="py-1 text-right font-medium text-xs">
                        {Math.abs(invoiceData.roundedOff).toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-t border-gray-200">
                      <td
                        colSpan={4}
                        className="py-2 text-left text-sm font-bold text-[#2A5783]">
                        Grand Total
                      </td>
                      <td className="py-2 text-right text-sm font-bold text-[#2A5783]">
                        ₹ {invoiceData.grandTotal.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Amount in Words */}
            <div className="mt-2 bg-gray-50 p-2 rounded border border-gray-200">
              <p className="font-medium text-xs text-[#2A5783]">
                {invoiceData.amountInWords}
              </p>
            </div>
          </div>

          {/* Right Side - Tax Summary */}
          <div>
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
              <div className="bg-[#2A5783] text-white font-medium px-3 py-1 text-xs">
                Tax Summary
              </div>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-[#2A5783]">
                      Tax Rate
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-[#2A5783]">
                      Taxable
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-[#2A5783]">
                      CGST
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-[#2A5783]">
                      SGST
                    </th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-[#2A5783]">
                      Total Tax
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-2 py-1.5 text-xs">
                      {invoiceData.taxRate}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">
                      {invoiceData.taxableAmount.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">
                      {invoiceData.cgstAmt.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">
                      {invoiceData.sgstAmt.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs font-medium">
                      {invoiceData.totalTax.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Bank Details and QR Code side-by-side */}
            <div className="grid grid-cols-2 gap-2">
              {/* Bank Details */}
              <div className="border border-gray-200 rounded-lg p-2">
                <div className="flex items-center mb-1">
                  <CreditCard size={14} className="text-[#2A5783] mr-1" />
                  <h4 className="font-bold text-[#2A5783] text-xs">
                    Bank Details
                  </h4>
                </div>
                <div className="text-xs space-y-0.5 text-gray-700 text-left">
                  <p>
                    <span className="inline-block font-medium">Name:</span>{" "}
                    {invoiceData.bankDetails.accountName}
                  </p>
                  <p>
                    <span className="inline-block font-medium">A/C:</span>{" "}
                    {invoiceData.bankDetails.accountNumber}
                  </p>
                  <p>
                    <span className="inline-block font-medium">IFSC:</span>{" "}
                    {invoiceData.bankDetails.ifscCode}
                  </p>
                  <p>
                    <span className="inline-block font-medium">Bank:</span>{" "}
                    {invoiceData.bankDetails.bankName}
                  </p>
                  <p>
                    <span className="inline-block font-medium">Branch:</span>{" "}
                    {invoiceData.bankDetails.branch}
                  </p>
                </div>
              </div>

              {/* QR Code */}
              <div className="border border-gray-200 rounded-lg p-2 flex flex-col items-center justify-center">
                <img
                  src={generateQrCodeUrl()}
                  alt="UPI QR Code"
                  className="w-24 h-24 mb-1"
                />
                <div className="flex items-center text-xs text-[#2A5783] mb-0.5">
                  <Scan size={12} className="mr-1" />
                  <span>Scan to pay via UPI</span>
                </div>
                <p className="text-xs text-center font-medium text-[#2A5783]">
                  {invoiceData.upiId}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Terms and Signature - Fixed uniform formatting */}
        <div className="p-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
          <div>
            <h4 className="font-bold mb-2 text-[#2A5783] text-xs">
              Terms & Conditions:
            </h4>
            <div className="text-xs text-gray-600">
              {invoiceData.terms.map((term, index) => (
                <div key={index} className="flex mb-2">
                  <div className="w-6 flex-shrink-0 text-left">
                    <span className="text-[#2A5783] font-medium">
                      {index + 1}.
                    </span>
                  </div>
                  <div className="flex-grow text-left">
                    <span>{term}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col justify-between">
            <div>
              <p className="font-bold mb-2 text-[#2A5783] text-xs text-right">
                Receiver's Signature:
              </p>
              <div className="h-12 border-b border-gray-300 mb-2"></div>
              <p className="text-[10px] text-gray-500 text-center">
                I/We hereby confirm that I/we have received the goods as per the
                invoice.
              </p>
            </div>
            <div className="text-right mt-4">
              <p className="font-medium text-xs text-[#2A5783]">
                For {invoiceData.firmName}
              </p>
              <div className="h-12"></div>
              <p className="font-medium text-xs text-[#2A5783]">
                Authorised Signatory
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
